import BaseBoosterBehavior, { IBaseBoosterBehaviorPlayMeta } from './BaseBoosterBehavior';
import CardStateHelper from '../CardStateHelper';
import { CardTask } from '../CardTasks';
import { BoosterType } from '../BoosterType';
import { track, TrackMethod } from '../../../Utility/Tracking/Track';
import { isNearEndGame } from '../../Level/LevelStateHelper';
import Game from '../../../Game';
import { FBTrackingEvent } from '../../../Utility/Tracking/FBTrackingEvent';
import { TransactionType } from '../../../Utility/Tracking/DataSet/Transaction';
import { IGameState } from '../../Game/IGameState';
import { InventoryStateHelper } from 'State/Player/Inventory/InventoryStateHelper';
import { CardType } from 'Prototype/Level/Card/CardType';
import { ValueCardData } from '../ICardState';

export interface IShuffleBoosterTargetData {
    cardID: string;
    oldValue: number;
    newValue: number | null;
}

export default class ShuffleBoosterBehavior extends BaseBoosterBehavior {
    public play(state: IGameState, meta: IBaseBoosterBehaviorPlayMeta): IGameState {
        // If there are not enough target cards, do nothing.
        if (!this.hasEnoughTargetCards(state)) return state;

        let cardsState = state.levelState.cardsState;

        const targets: IShuffleBoosterTargetData[] = this.getTargetCards(state).map(cardID => ({
            cardID: cardID,
            oldValue: (state.levelState.cardsState.cards[cardID] as ValueCardData).value,
            newValue: null,
        }));

        cardsState = CardStateHelper.shuffleValueCards(cardsState);

        targets.forEach(target => {
            target.newValue = (cardsState.cards[target.cardID] as ValueCardData).value;
        });

        // Trigger shuffle animation task.
        cardsState = CardStateHelper.setStateTaskData(cardsState, {
            task: CardTask.SHUFFLE,
            cards: targets.map(t => t.cardID),
            targets: targets,
        });

        state = {
            ...state,
            levelState: {
                ...state.levelState,
                cardsState: cardsState,
            },
        };

        state = this.incrementBoosterUse(state, BoosterType.SHUFFLE);

        return this.consumeItemOrCoins(state);
    }

    public getTargetCards(state: IGameState): string[] {
        const cardsState = { ...state.levelState.cardsState };

        const valueCards: string[] = CardStateHelper.getValueCards(state.levelState.cardsState);

        return valueCards.filter(cardID => {
            const behavior = CardStateHelper.getCardBehavior(cardsState, cardID);
            return behavior.isFaceUp(cardsState) && !behavior.isBlocked(cardsState);
        });
    }

    public hasEnoughTargetCards(state: IGameState): boolean {
        const cardsInPlayMinusGold: string[] = CardStateHelper.getCardsInPlay(state.levelState.cardsState, {
            ignoreCardTypes: [CardType.gold],
        });
        // Must have at least two cards in play and one of those faced up to swap.
        return this.getTargetCards(state).length > 0 && cardsInPlayMinusGold.length > this.getTargetCards(state).length;
    }

    protected getPrice(state: IGameState): number {
        return state.levelState.boosterPrices[BoosterType.SHUFFLE];
    }

    protected getInventoryItem(state: IGameState): number {
        return state.playerState.inventory[BoosterType.SHUFFLE];
    }

    protected spendInventoryItem(state: IGameState): IGameState {
        track('transactionType', TransactionType.INVENTORY);

        // TODO: use InventoryStateHelper.consumeInventory instead
        return {
            ...state,
            playerState: InventoryStateHelper.setBoosterShuffle(
                state.playerState,
                state.playerState.inventory[BoosterType.SHUFFLE] - 1,
                { queueTask: false }
            ),
        };
    }

    protected spendCoins(state: IGameState): IGameState {
        super.trackCoinsSpent(state, 'tChangerCoins');
        track('tChangerReceived', 1, TrackMethod.ADD);
        state = super.spendCoins(state);
        track('tChangerUsed', 1, TrackMethod.ADD);
        return state;
    }

    protected consumeItemOrCoins(state: IGameState): IGameState {
        Game.sdk.gameTracking(FBTrackingEvent.TapOnElectricFan);
        track('mChangerUsed', 1, TrackMethod.ADD);

        if (isNearEndGame(state)) {
            track('mNumBoostersUsedLossAversion', 1, TrackMethod.ADD);
        }

        return super.consumeItemOrCoins(state);
    }
}
